import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Text, Badge, Group, Stack, Card, ScrollArea, Skeleton } from '@mantine/core'
import { IconGripVertical } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { notifications } from '@mantine/notifications'
import { PriorityBadge } from '@/components/chamados'

interface KanbanTicket {
  id: number
  title: string
  status: string
  priority?: string
  protocol_number?: string
  assignee_name?: string
  category_name?: string
}

interface KanbanBoardProps {
  tickets: KanbanTicket[]
  tenantId: number
  loading?: boolean
}

// Colunas do ciclo de vida v2 (7 etapas). `statuses` = status que caem nessa
// coluna; `dropStatus` = status aplicado ao soltar um card na coluna (drag).
// Cores coerentes com o StatusBadge (SP1).
interface KanbanColumn {
  key: string
  label: string
  color: string
  statuses: string[]
  dropStatus: string
}

const COLUMNS: KanbanColumn[] = [
  { key: 'novo',             label: 'Novo',            color: '#4dabf7', statuses: ['requested', 'approved'],        dropStatus: 'requested' },
  { key: 'in_triage',        label: 'Em Triagem',      color: '#4dabf7', statuses: ['in_triage'],                    dropStatus: 'in_triage' },
  { key: 'in_progress',      label: 'Em Atendimento',  color: '#7c81e6', statuses: ['in_progress'],                  dropStatus: 'in_progress' },
  { key: 'waiting_customer', label: 'Aguard. Cliente', color: '#f59c1a', statuses: ['waiting_customer'],             dropStatus: 'waiting_customer' },
  { key: 'blocked',          label: 'Bloqueado',       color: '#1c7ed6', statuses: ['blocked'],                      dropStatus: 'blocked' },
  { key: 'validation',       label: 'Validação',       color: '#ae3ec9', statuses: ['pending_customer_validation'],  dropStatus: 'pending_customer_validation' },
  { key: 'closed',           label: 'Fechado',         color: '#495057', statuses: ['closed'],                       dropStatus: 'closed' },
]

// Status que NÃO caem em nenhuma coluna do quadro (fallback): mantidos fora do
// board para não desaparecerem silenciosamente da contagem.
const MAPPED_STATUSES = new Set(COLUMNS.flatMap((c) => c.statuses))

function KanbanCard({
  ticket,
  onDragStart,
}: {
  ticket: KanbanTicket
  onDragStart: (e: React.DragEvent, ticketId: number) => void
}) {
  const navigate = useNavigate()

  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, ticket.id)}
      onClick={() => navigate(`/chamados/${ticket.id}`)}
      shadow="xs"
      radius="sm"
      p="sm"
      mb="xs"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: 'var(--ai-border-light)',
        transition: 'all var(--ai-transition-fast)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--ai-border-medium)'
        e.currentTarget.style.transform = 'translateY(-1px)'
        e.currentTarget.style.boxShadow = 'var(--ai-shadow-md)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--ai-border-light)'
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <Group gap={6} mb={4}>
        <IconGripVertical size={12} style={{ color: '#adb5bd', flexShrink: 0 }} />
        {/* Texto, nao Badge: o label do Badge do Mantine trunca com reticencias
            (`overflow: hidden; text-overflow: ellipsis`), e protocolo truncado e
            inutil — ele existe para ser copiado e buscado. Com 24-27 chars
            (spec 2026-07-28) numa coluna de 272px a margem sumiu. `flexShrink: 0`
            impede que o Group o comprima; `nowrap` impede a quebra nos hifens. */}
        {ticket.protocol_number && (
          <Text size="xs" ff="monospace" fw={600} c="blue.7"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {ticket.protocol_number}
          </Text>
        )}
      </Group>
      <Text size="sm" fw={500} lineClamp={2} mb={6}>{ticket.title}</Text>
      <Group gap={6} wrap="wrap">
        {ticket.priority && <PriorityBadge priority={ticket.priority} />}
        {ticket.category_name && (
          <Badge size="xs" variant="light" color="gray">{ticket.category_name}</Badge>
        )}
      </Group>
      {ticket.assignee_name && (
        <Text size="xs" c="dimmed" mt={4}>{ticket.assignee_name}</Text>
      )}
    </Card>
  )
}

export function KanbanBoard({ tickets, tenantId, loading = false }: KanbanBoardProps) {
  const qc = useQueryClient()
  const [draggedTicketId, setDraggedTicketId] = useState<number | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null)

  const statusMutation = useMutation({
    mutationFn: async ({ ticketId, newStatus }: { ticketId: number; newStatus: string }) => {
      await api.patch(`/v1/tenants/${tenantId}/workflow/${ticketId}/status`, {
        status: newStatus,
      })
    },
    onSuccess: () => {
      invalidarPendenciasDoChamado(qc)  // arrastar para validação entra na aba e no menu
      notifications.show({ title: 'Status atualizado', message: 'Chamado movido com sucesso', color: 'teal' })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? 'Transição de status inválida'
      notifications.show({ title: 'Erro', message: String(detail), color: 'red' })
    },
  })

  const handleDragStart = (e: React.DragEvent, ticketId: number) => {
    setDraggedTicketId(ticketId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverColumn(columnKey)
  }

  const handleDragLeave = () => {
    setDragOverColumn(null)
  }

  const handleDrop = (e: React.DragEvent, column: KanbanColumn) => {
    e.preventDefault()
    setDragOverColumn(null)
    if (draggedTicketId === null) return

    const ticket = tickets.find(t => t.id === draggedTicketId)
    // Já está nesta coluna (qualquer status mapeado nela): não faz nada.
    if (!ticket || column.statuses.includes(ticket.status)) return

    statusMutation.mutate({ ticketId: draggedTicketId, newStatus: column.dropStatus })
    setDraggedTicketId(null)
  }

  if (loading) {
    return (
      <ScrollArea scrollbarSize={6}>
        <Group gap="md" align="flex-start" wrap="nowrap" style={{ minWidth: COLUMNS.length * 288 }}>
          {COLUMNS.map(col => (
            <Box key={col.key} style={{ width: 272, flexShrink: 0 }}>
              <Skeleton height={30} mb="sm" />
              <Skeleton height={80} mb="xs" />
              <Skeleton height={80} mb="xs" />
            </Box>
          ))}
        </Group>
      </ScrollArea>
    )
  }

  return (
    <ScrollArea scrollbarSize={6} type="auto">
      <Group gap="md" align="flex-start" wrap="nowrap" style={{ minWidth: COLUMNS.length * 288, paddingBottom: 4 }}>
        {COLUMNS.map(col => {
          const columnTickets = tickets.filter(t => col.statuses.includes(t.status))
          const isDragOver = dragOverColumn === col.key

          return (
            <Box
              key={col.key}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col)}
              style={{
                width: 272,
                flexShrink: 0,
                backgroundColor: isDragOver ? 'rgba(9,141,147,0.05)' : 'var(--ai-body-bg)',
                borderRadius: 'var(--mantine-radius-md)',
                border: isDragOver ? '2px dashed var(--ai-brand-verde)' : '2px solid transparent',
                padding: 8,
                minHeight: 400,
                transition: 'all var(--ai-transition-fast)',
              }}
            >
              {/* Column header */}
              <Group justify="space-between" mb="sm" px={4}>
                <Group gap={6}>
                  <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: col.color }} />
                  <Text size="sm" fw={600}>{col.label}</Text>
                </Group>
                <Badge size="sm" variant="light" color="gray" circle>
                  {columnTickets.length}
                </Badge>
              </Group>

              {/* Column body */}
              <Stack gap={0}>
                {columnTickets.length === 0 ? (
                  <Box style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    border: '1px dashed var(--ai-border-light)',
                    borderRadius: 'var(--mantine-radius-sm)',
                  }}>
                    <Text size="xs" c="dimmed">Nenhum chamado</Text>
                  </Box>
                ) : (
                  columnTickets.map(ticket => (
                    <KanbanCard
                      key={ticket.id}
                      ticket={ticket}
                      onDragStart={handleDragStart}
                    />
                  ))
                )}
              </Stack>
            </Box>
          )
        })}
      </Group>
    </ScrollArea>
  )
}
